import { type ReactNode, useCallback } from 'react'
import {
  ModalDialog,
  ModalDialogActions,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { Button } from '@/components/Button'
import { useSearchParams } from 'react-router'

export interface ExpectationsDialogProps {
  /**
   * Content to display in the dialog body.
   */
  children?: ReactNode
  /**
   * Title of the dialog.
   */
  title?: string
  /**
   * Description text for the dialog.
   */
  description?: string
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean
  /**
   * Called when the dialog is closed.
   */
  onClose?: () => unknown
  /**
   * Test ID attribute for the dialog.
   */
  testId?: string
  /**
   * Test ID attribute for the confirm button.
   */
  confirmTestId?: string
}

/**
 * Dialog used for displaying a list of steps, conveying expectations of the
 * flow to the user.
 *
 * @example
 *  <ExpectationsDialog>
 *    <StepList>
 *      <Step/>
 *    </StepList>
 *  </ExpectationsDialog>
 *
 */
export function ExpectationsDialog({
  title,
  description,
  isOpen,
  onClose,
  children,
  testId,
  confirmTestId,
}: ExpectationsDialogProps) {
  title ??= 'What you can expect next'
  description ??=
    'We understand that this might be a little daunting, but we’re here to guide you every step of the way. Let’s uncover how this will work.'
  return (
    <ModalDialog testId={testId} size="lg" isOpen={isOpen} onClose={onClose}>
      <ModalDialogHeader>
        <ModalDialogTitle>{title}</ModalDialogTitle>
        <ModalDialogDescription>{description}</ModalDialogDescription>
      </ModalDialogHeader>
      <ModalDialogContent>{children}</ModalDialogContent>
      <ModalDialogFooter>
        <ModalDialogActions>
          <Button label="Got it" onPress={onClose} testId={confirmTestId} />
        </ModalDialogActions>
      </ModalDialogFooter>
    </ModalDialog>
  )
}

/**
 * State management for the Expectations dialog using search params.
 */
export function useExpectationsDialog() {
  const [searchParams, setSearchParams] = useSearchParams()

  const isOpen = searchParams.get('dialog') === 'expectations'
  const open = useCallback(() => {
    setSearchParams(
      (s) => {
        s.set('dialog', 'expectations')
        return s
      },
      { replace: true, preventScrollReset: true },
    )
  }, [setSearchParams])

  const close = useCallback(() => {
    setSearchParams(
      (s) => {
        s.delete('dialog')
        return s
      },
      { replace: true, preventScrollReset: true },
    )
  }, [setSearchParams])

  return {
    isOpen,
    open,
    close,
  }
}
